import React, { useState, useEffect, useCallback } from 'react';
import { Search, Clock, Plus, X, Check } from 'lucide-react';
import LearningComponentsService from '../../services/learningComponentsService';
import { COMPONENT_CATEGORIES, COMPONENT_TYPES } from '../../data/learningComponentsLibrary';
import './LearningComponentPicker.css';

const LearningComponentPicker = ({ 
  onComponentsSelected, 
  onConfirmSelection,
  selectedComponents = [], 
  maxComponents = 10,
  allowedCategories = null,
  allowedTypes = null 
}) => {
  const [components, setComponents] = useState([]);
  const [filteredComponents, setFilteredComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [selectedTag, setSelectedTag] = useState('all');
  const [groupBy, setGroupBy] = useState('none'); // 'none', 'category', 'type', 'difficulty'
  const [showPreview, setShowPreview] = useState(null);
  const [currentSelection, setCurrentSelection] = useState(selectedComponents);

  useEffect(() => {
    loadComponents();
  }, []);

  const filterComponents = useCallback(() => {
    let filtered = [...components];

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(comp => 
        comp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        comp.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        comp.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(comp => comp.category === selectedCategory);
    }

    // Filter by type
    if (selectedType !== 'all') {
      filtered = filtered.filter(comp => comp.type === selectedType);
    }

    // Filter by difficulty
    if (selectedDifficulty !== 'all') {
      filtered = filtered.filter(comp => comp.difficulty === selectedDifficulty);
    }

    // Filter by tag
    if (selectedTag !== 'all') {
      filtered = filtered.filter(comp => 
        comp.tags && comp.tags.includes(selectedTag)
      );
    }

    // Apply allowed categories if specified
    if (allowedCategories && allowedCategories.length > 0) {
      filtered = filtered.filter(comp => allowedCategories.includes(comp.category));
    }

    // Apply allowed types if specified
    if (allowedTypes && allowedTypes.length > 0) {
      filtered = filtered.filter(comp => allowedTypes.includes(comp.type));
    }

    setFilteredComponents(filtered);
  }, [
    components,
    searchTerm,
    selectedCategory,
    selectedType,
    selectedDifficulty,
    selectedTag,
    allowedCategories,
    allowedTypes
  ]);

  useEffect(() => {
    filterComponents();
  }, [filterComponents]);

  const loadComponents = async () => {
    try {
      setLoading(true);
      const allComponents = await LearningComponentsService.getAllComponents();
      setComponents(allComponents);
    } catch (error) {
      console.error('Error loading components:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get all unique tags from components
  const getAllTags = () => {
    const tagSet = new Set();
    components.forEach(comp => {
      if (comp.tags) {
        comp.tags.forEach(tag => tagSet.add(tag));
      }
    });
    return Array.from(tagSet).sort();
  };

  // Group components by specified criteria
  const getGroupedComponents = () => {
    if (groupBy === 'none') {
      return { 'All Components': filteredComponents };
    }

    const grouped = {};
    filteredComponents.forEach(comp => {
      let key;
      switch (groupBy) {
        case 'category':
          key = comp.category || 'Uncategorized';
          break;
        case 'type':
          key = comp.type || 'Unknown';
          break;
        case 'difficulty':
          key = comp.difficulty || 'unknown';
          break;
        default:
          key = 'All Components';
      }
      
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(comp);
    });

    return grouped;
  };

  const toggleComponentSelection = (component) => {
    const isSelected = currentSelection.some(comp => comp.id === component.id);
    
    if (isSelected) {
      // Remove from selection
      const newSelection = currentSelection.filter(comp => comp.id !== component.id);
      setCurrentSelection(newSelection);
      onComponentsSelected(newSelection);
    } else {
      // Add to selection
      if (currentSelection.length >= maxComponents) {
        alert(`You can select maximum ${maxComponents} components`);
        return;
      }
      const newSelection = [...currentSelection, component];
      setCurrentSelection(newSelection);
      onComponentsSelected(newSelection);
    }
  };

  const handleDone = () => {
    if (onConfirmSelection) {
      onConfirmSelection(currentSelection);
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return '#4CAF50';
      case 'intermediate': return '#FF9800';
      case 'advanced': return '#F44336';
      default: return '#9E9E9E';
    }
  };

  const getDifficultyIcon = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return '🌱';
      case 'intermediate': return '🚀';
      case 'advanced': return '💎';
      default: return '📚';
    }
  };

  const getTypeIcon = (type) => {
    const typeInfo = COMPONENT_TYPES.find(t => t.name.toLowerCase() === type);
    return typeInfo ? typeInfo.icon : '📦';
  };

  const getCategoryInfo = (category) => {
    return COMPONENT_CATEGORIES.find(c => c.name === category);
  };

  if (loading) {
    return (
      <div className="component-picker-loading">
        <div className="loading-spinner"></div>
        <p>Loading learning components...</p>
      </div>
    );
  }

  return (
    <div className="learning-component-picker">
      {/* Header */}
      <div className="picker-header">
        <div>
          <h3>Choose Learning Components</h3>
          <p className="selection-count">
            {currentSelection.length} of {maxComponents} selected
          </p>
        </div>
        {onConfirmSelection && (
          <button 
            className="done-btn" 
            onClick={handleDone}
            disabled={currentSelection.length === 0}
          >
            <Check size={16} />
            Done ({currentSelection.length})
          </button>
        )}
      </div>

      {/* Search and Filters */}
      <div className="picker-filters">
        <div className="search-bar">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search components..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-controls">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="all">All Categories</option>
            {COMPONENT_CATEGORIES.map(cat => (
              <option key={cat.name} value={cat.name}>
                {cat.icon} {cat.name}
              </option>
            ))}
          </select>

          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
          >
            <option value="all">All Types</option>
            {COMPONENT_TYPES.map(type => (
              <option key={type.name} value={type.name.toLowerCase()}>
                {type.icon} {type.name}
              </option>
            ))}
          </select>

          <select
            value={selectedDifficulty}
            onChange={(e) => setSelectedDifficulty(e.target.value)}
          >
            <option value="all">All Levels</option>
            <option value="beginner">🌱 Beginner</option>
            <option value="intermediate">🚀 Intermediate</option>
            <option value="advanced">💎 Advanced</option>
          </select>

          <select
            value={selectedTag}
            onChange={(e) => setSelectedTag(e.target.value)}
          >
            <option value="all">All Tags</option>
            {getAllTags().map(tag => (
              <option key={tag} value={tag}>
                🏷️ {tag}
              </option>
            ))}
          </select>

          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
          >
            <option value="none">No Grouping</option>
            <option value="category">Group by Category</option>
            <option value="type">Group by Type</option>
            <option value="difficulty">Group by Level</option>
          </select>
        </div>
      </div>

      {/* Selected Components */}
      {currentSelection.length > 0 && (
        <div className="selected-components">
          <h4>Selected Components</h4>
          <div className="selected-list">
            {currentSelection.map(comp => (
              <div key={comp.id} className="selected-item">
                <span>{getTypeIcon(comp.type)} {comp.name}</span>
                <button
                  onClick={() => toggleComponentSelection(comp)}
                  className="remove-btn"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Components Grid */}
      <div className="components-grid-container">
        {Object.keys(getGroupedComponents()).length === 0 ? (
          <div className="no-components">
            <p>No components found matching your criteria.</p>
          </div>
        ) : (
          Object.entries(getGroupedComponents()).map(([groupName, groupComponents]) => (
            <div key={groupName} className="component-group">
              {groupBy !== 'none' && (
                <div className="group-header">
                  <h3>
                    {groupBy === 'category' && getCategoryInfo(groupName)?.icon}
                    {groupBy === 'type' && getTypeIcon(groupName)}
                    {groupBy === 'difficulty' && getDifficultyIcon(groupName)}
                    {groupName}
                  </h3>
                  <span className="group-count">{groupComponents.length} components</span>
                </div>
              )}
              
              <div className="components-grid">
                {groupComponents.map(component => {
                  const isSelected = currentSelection.some(comp => comp.id === component.id);
                  const categoryInfo = getCategoryInfo(component.category);
                  
                  return (
                    <div
                      key={component.id}
                      className={`component-card ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggleComponentSelection(component)}
                    >
                      <div className="card-header">
                        <div className="component-type">
                          {getTypeIcon(component.type)}
                        </div>
                        <div className="selection-indicator">
                          {isSelected ? <Check size={20} /> : <Plus size={20} />}
                        </div>
                      </div>

                      <div className="card-content">
                        <h4>{component.name}</h4>
                        <p className="description">{component.description}</p>
                        
                        <div className="component-meta">
                          <div className="meta-item">
                            <div 
                              className="category-badge"
                              style={{ backgroundColor: categoryInfo?.color || '#9E9E9E' }}
                            >
                              {categoryInfo?.icon} {component.category}
                            </div>
                          </div>
                          
                          <div className="meta-item">
                            <div 
                              className="difficulty-badge"
                              style={{ backgroundColor: getDifficultyColor(component.difficulty) }}
                            >
                              {component.difficulty}
                            </div>
                          </div>
                          
                          <div className="meta-item">
                            <Clock size={14} />
                            <span>{component.estimatedTime}m</span>
                          </div>
                        </div>

                        <div className="component-tags">
                          {component.tags && component.tags.slice(0, 3).map(tag => (
                            <span key={tag} className="tag">{tag}</span>
                          ))}
                          {component.tags && component.tags.length > 3 && (
                            <span className="tag">+{component.tags.length - 3}</span>
                          )}
                        </div>
                      </div>

                      <div className="card-actions">
                        <button
                          className="preview-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowPreview(component);
                          }}
                        >
                          Preview
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="preview-modal">
          <div className="preview-content">
            <div className="preview-header">
              <h3>{showPreview.name}</h3>
              <button
                className="close-btn"
                onClick={() => setShowPreview(null)}
              >
                <X size={24} />
              </button>
            </div>

            <div className="preview-body">
              <p className="preview-description">{showPreview.description}</p>
              
              <div className="preview-details">
                <div className="detail-row">
                  <strong>Category:</strong> 
                  <span>{getCategoryInfo(showPreview.category)?.icon} {showPreview.category}</span>
                </div>
                <div className="detail-row">
                  <strong>Type:</strong> 
                  <span>{getTypeIcon(showPreview.type)} {showPreview.type}</span>
                </div>
                <div className="detail-row">
                  <strong>Difficulty:</strong> 
                  <span 
                    className="difficulty-badge"
                    style={{ backgroundColor: getDifficultyColor(showPreview.difficulty) }}
                  >
                    {showPreview.difficulty}
                  </span>
                </div>
                <div className="detail-row">
                  <strong>Estimated Time:</strong> 
                  <span><Clock size={14} /> {showPreview.estimatedTime} minutes</span>
                </div>
              </div>

              <div className="preview-config">
                <h4>Features</h4>
                <div className="config-list">
                  {Object.entries(showPreview.config).map(([key, value]) => (
                    <div key={key} className="config-item">
                      <Check size={16} />
                      <span>{key}: {Array.isArray(value) ? value.join(', ') : value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="preview-tags">
                <h4>Tags</h4>
                <div className="tags-list">
                  {showPreview.tags.map(tag => (
                    <span key={tag} className="tag">{tag}</span>
                  ))}
                </div>
              </div>
            </div>

            <div className="preview-footer">
              <button
                className="select-btn"
                onClick={() => {
                  toggleComponentSelection(showPreview);
                  setShowPreview(null);
                }}
              >
                {currentSelection.some(comp => comp.id === showPreview.id) ? 'Remove' : 'Select'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LearningComponentPicker;
